<template>
  <Card title="Compliance & Monitoring" customClass="p-6 relative vld-parent">
    <loading :active.sync="loading" :is-full-page="false" />

    <!-- Top: Monitored Assets & Cost Analysis -->
    <div class="flex flex-col md:flex-row justify-between w-full gap-4">
      <!-- Monitored Assets -->
      <div class="bg-gray-100 rounded-lg p-4 flex-1">
        <h3 class="text-md font-semibold mb-2">
          Monitored Assets*
          <span
            :class="{
              'bg-red-100 text-red-700': criticalCount > 0,
              'bg-gray-300 text-gray-700': criticalCount === 0
            }"
            class="text-xs font-bold ml-2 py-1 px-2 rounded-full"
          >
            {{ criticalCount !== null ? criticalCount + ' critical' : 'N/A' }}
          </span>
        </h3>
        <p class="text-sm text-gray-700">
          {{ displayOrNA(monitored) }} monitored ·
          {{ displayOrNA(unmonitored) }} unmonitored ·
          {{ displayOrNA(excluded) }} excluded
        </p>
        <div class="mt-2 h-3 bg-gray-300 rounded-full overflow-hidden">
          <div
            v-for="(service, namespace) in serviceMetrics"
            :key="namespace"
            :class="namespaceColor(namespace)"
            :style="{ width: (service.count / totalAssets * 100) + '%' }"
            class="h-full inline-block"
          ></div>
        </div>
        <div class="flex justify-between text-xs text-gray-600 mt-1">
          <span>Total: {{ displayOrNA(totalAssets) }} assets</span>
          <span>{{ coverage !== null ? coverage.toFixed(1) : 'N/A' }}% coverage</span>
        </div>
      </div>

      <!-- Cost Analysis -->
      <div class="bg-gray-100 rounded-lg p-4 flex-1">
        <h3 class="text-md font-semibold mb-2">
          Cost Analysis
          <span
            :class="{
              'text-green-600': changePercentage < 0,
              'text-red-600': changePercentage > 0
            }"
            class="text-xs font-bold ml-2"
          >
            {{ changePercentage !== null ? formatPercentage(changePercentage) : 'N/A' }}
          </span>
        </h3>
        <div class="text-sm text-gray-700">
          <p>
            <strong>Current Month:</strong>
            <span class="float-right font-semibold text-black">
              {{ formatCurrency(currentMonthCost) }}
            </span>
          </p>
          <p class="mt-1">
            <strong>Previous Month:</strong>
            <span class="float-right text-gray-800">
              {{ formatCurrency(previousMonthCost) }}
            </span>
          </p>
        </div>
      </div>
    </div>

    <!-- Middle: Service Details -->
    <div class="mt-6">
      <div
        v-for="(service, namespace) in serviceMetrics"
        :key="namespace"
        class="border-t pt-4"
      >
        <details class="mb-2 group">
          <summary class="font-semibold cursor-pointer flex items-center">
            <span class="mr-2">{{ namespace.replace('AWS/', '') }} ({{ service.count }})</span>
            <svg class="w-4 h-4 ml-auto group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </summary>
          <ul class="text-sm text-gray-600 mt-2">
            <li>
              <strong>Monitoring Ratings:</strong>
              <span v-for="(count, rating) in service.ratings" :key="rating" class="mr-2">
                {{ rating }}: <span :class="ratingColor(rating)">{{ count }}</span>
              </span>
            </li>
            <li>
              <strong>Alarm Coverage:</strong>
              {{ service.activeAlarms }} / {{ service.recommendedAlarms }}
            </li>
            <li v-if="costs[namespace]">
              <strong>Cost:</strong>
              {{ formatCurrency(costs[namespace]) }}
            </li>
          </ul>
        </details>
      </div>
    </div>

    <!-- Bottom: Link -->
    <router-link
      :to="`/${id}/monitoring`"
      class="inline-flex items-center px-5 py-3 mt-4 bg-white text-indigo-600 font-semibold border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition duration-200"
    >
      View Detailed Compliance
      <svg xmlns="http://www.w3.org/2000/svg" class="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </router-link>
  </Card>
</template>

<script>
import Card from '../Card.vue'
import HTTP from '@/common/http'
import Loading from 'vue-loading-overlay'
import { mapGetters } from 'vuex'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  name: 'MonitoredCard',
  components: { Card, Loading },
  props: {
    id: { type: String, default: 'AllContracts' }
  },
  data () {
    return {
      loading: false,
      monitored: null,
      unmonitored: null,
      excluded: null,
      criticalCount: null,
      currentMonthCost: null,
      previousMonthCost: null,
      changePercentage: null,
      serviceMetrics: {},
      costs: {}
    }
  },
  computed: {
    ...mapGetters(['activeType']),
    totalAssets () {
      return Object.values(this.serviceMetrics).reduce((sum, service) => sum + service.count, 0)
    },
    coverage () {
      if (!this.totalAssets) return 0
      return (this.monitored / this.totalAssets) * 100
    }
  },
  methods: {
    displayOrNA (val) {
      return (val !== null && val !== undefined && val !== '') ? val : 'N/A'
    },
    formatPercentage (value) {
      if (typeof value !== 'number') return 'N/A'
      return (value >= 0 ? '+' : '') + value.toFixed(1) + '%'
    },
    formatCurrency (value) {
      if (value === null || value === undefined) return 'N/A'
      return '$' + Number(value).toLocaleString()
    },
    ratingColor (rating) {
      return {
        'text-red-600': rating === 'high',
        'text-yellow-600': rating === 'medium',
        'text-green-600': rating === 'low',
        'text-gray-600': rating === 'unrated'
      }
    },
    namespaceColor (namespace) {
      const colorMap = {
        'AWS/EC2': 'bg-aws-ec2',
        'AWS/RDS': 'bg-aws-rds',
        'AWS/ES': 'bg-aws-es',
        'AWS/EFS': 'bg-aws-efs'
      }
      return colorMap[namespace] || 'bg-gray-300'
    },
    async fetchMonitoringData () {
      this.loading = true
      try {
        // const response = await HTTP.get(`/monitoring/contract/JaredCorp`) Hardcoded line to let page load properly
        const response = await HTTP.get(`/monitoring/contract/${this.id}`)
        const reports = response.data.monitoring || []

        let monitored = 0
        let unmonitored = 0
        let criticalCount = 0
        const serviceMetrics = {}

        reports.forEach(report => {
          const alarms = Array.isArray(report.metric_alarms) ? report.metric_alarms : []

          alarms.forEach(alarm => {
            const namespace = alarm.Namespace || 'Unknown'
            const rating = (alarm.MonitoringRating || 'unrated').toLowerCase()
            const hasAlarms = Array.isArray(alarm.MetricAlarms) && alarm.MetricAlarms.length > 0

            if (hasAlarms) monitored++
            else unmonitored++

            if (rating === 'high') criticalCount++

            if (!serviceMetrics[namespace]) {
              serviceMetrics[namespace] = {
                count: 0,
                activeAlarms: 0,
                recommendedAlarms: 0,
                ratings: {}
              }
            }

            serviceMetrics[namespace].count++
            serviceMetrics[namespace].activeAlarms += Array.isArray(alarm.MetricAlarms) ? alarm.MetricAlarms.length : 0
            serviceMetrics[namespace].recommendedAlarms += Array.isArray(alarm.RecommendedAlarms) ? alarm.RecommendedAlarms.length : 0
            serviceMetrics[namespace].ratings[rating] = (serviceMetrics[namespace].ratings[rating] || 0) + 1
          })
        })

        this.monitored = monitored
        this.unmonitored = unmonitored
        this.criticalCount = criticalCount
        this.serviceMetrics = serviceMetrics
      } catch (error) {
        this.monitored = this.unmonitored = this.excluded = this.criticalCount = null
        this.serviceMetrics = {}
      } finally {
        this.loading = false
      }
    },
    async fetchCostData () {
      try {
        const response = await HTTP.get(`/costs/metrics/${this.activeType}/${this.id}/month`)
        const metrics = Array.isArray(response.data.metrics) ? response.data.metrics : []
        const latest = metrics.length > 0 ? metrics[metrics.length - 1] : {}
        const previous = metrics.length > 1 ? metrics[metrics.length - 2] : {}

        const latestContracts = latest.contracts && latest.contracts[0]
        const previousContracts = previous.contracts && previous.contracts[0]

        this.currentMonthCost = (latestContracts && typeof latestContracts.cost === 'number')
          ? latestContracts.cost
          : null

        this.previousMonthCost = (previousContracts && typeof previousContracts.cost === 'number')
          ? previousContracts.cost
          : null

        if (this.previousMonthCost && this.currentMonthCost !== null) {
          this.changePercentage = ((this.currentMonthCost - this.previousMonthCost) / this.previousMonthCost) * 100
        } else {
          this.changePercentage = null
        }

        const costs = {}
        if (Array.isArray(latest.accounts)) {
          latest.accounts.forEach(account => {
            if (Array.isArray(account.services)) {
              account.services.forEach(service => {
                const serviceName = service.service ? service.service.toUpperCase() : 'UNKNOWN'
                const namespace = `AWS/${serviceName}`
                const amount = typeof service.amount === 'number' ? service.amount : 0
                costs[namespace] = (costs[namespace] || 0) + amount
              })
            }
          })
        }

        this.costs = costs
      } catch (error) {
        this.costs = {}
        this.currentMonthCost = this.previousMonthCost = this.changePercentage = null
      }
    }
  },
  async created () {
    await this.fetchMonitoringData()
    await this.fetchCostData()
  }
}

</script>

<style scoped>
.bg-aws-ec2 { background-color: #ff9900; }
.bg-aws-rds { background-color: #00a1c9; }
.bg-aws-es { background-color: #f46e32; }
.bg-aws-efs { background-color: #6c3082; }
</style>



// WEBPACK FOOTER //
// src/components/Cards/InfoCards/MonitoredCard.vue